import React, { useState } from "react";
import ReactDOM from "react-dom";
import { FiX, FiCheck } from "react-icons/fi";
import { createCheckoutSession } from "../../services/paymentsService";
import type { Plan, CreateCheckoutSessionResponse } from "../../types/payments";
import { useSubscription } from "../../hooks/useSubscription";

// Type-safe icon wrapper
const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;
const CheckIcon = FiCheck as React.FC<React.SVGProps<SVGSVGElement>>;

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Plan definitions with 4 tiers
const planDefinitions: Array<{
  id: Plan;
  title: string;
  price: string;
  period?: string;
  description: string;
  features: string[];
  buttonText: string;
  isContactSales?: boolean;
}> = [
  {
    id: "free",
    title: "Free",
    price: "Free",
    description: "Get started with limited features",
    features: ["1 manual send/week", "Basic analytics"],
    buttonText: "Current Plan",
  },
  {
    id: "basic",
    title: "Basic",
    price: "$50",
    period: "/month",
    description: "Unlimited sends and automations",
    features: ["Unlimited sends", "Unlimited automations", "Advanced analytics"],
    buttonText: "Upgrade to Basic",
  },
  {
    id: "pro",
    title: "Pro",
    price: "$75",
    period: "/month",
    description: "Full access to all features",
    features: ["Everything in Basic", "Data room", "Deck storage", "Custom API Keys"],
    buttonText: "Upgrade to Pro",
  },
  {
    id: "enterprise",
    title: "Enterprise",
    price: "Contact Sales",
    description: "Custom solutions for your team",
    features: ["Everything in Pro", "Dedicated support", "Custom integrations"],
    buttonText: "Contact Sales",
    isContactSales: true,
  },
];

// Plan tier hierarchy for comparison
const planHierarchy: Plan[] = ["free", "basic", "pro", "enterprise"];

/**
 * UpgradeModal - Modal for upgrading subscription plan
 * Displays pricing plans and handles Stripe checkout
 */
const UpgradeModal: React.FC<UpgradeModalProps> = ({ isOpen, onClose }) => {
  const { features } = useSubscription();
  const currentPlanTier = features.planTier;
  const [selectedPlan, setSelectedPlan] = useState<Plan>("basic");
  const [loadingPlan, setLoadingPlan] = useState<Plan | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSelectPlan = (planId: Plan) => {
    if (!loadingPlan) {
      setSelectedPlan(planId);
    }
  };

  const handleContactSales = () => {
    window.location.href = "mailto:sales@sendupdates.ai?subject=Enterprise%20Plan%20Inquiry";
  };

  const isCurrentPlan = (planId: Plan) => planId === currentPlanTier;

  const isDowngrade = (planId: Plan) => {
    const currentIndex = planHierarchy.indexOf(currentPlanTier);
    const targetIndex = planHierarchy.indexOf(planId);
    return targetIndex < currentIndex;
  };

  const handleCheckout = async (planId: Plan) => {
    const plan = planDefinitions.find((p) => p.id === planId);

    // Handle Enterprise (Contact Sales)
    if (plan?.isContactSales) {
      handleContactSales();
      return;
    }

    // Current plan - just close
    if (isCurrentPlan(planId)) {
      onClose();
      return;
    }

    // Free plan - no Stripe needed
    if (planId === "free") {
      onClose();
      return;
    }

    setLoadingPlan(planId);
    setError(null);

    try {
      // Store the current URL so we can redirect back after payment
      localStorage.setItem("payment_return_url", window.location.pathname + window.location.search);

      const response: CreateCheckoutSessionResponse =
        await createCheckoutSession(planId, "upgrade");

      // Use the checkout URL directly instead of redirectToCheckout
      if (response.url) {
        window.location.href = response.url;
      } else {
        throw new Error("No checkout URL returned from server.");
      }
    } catch (err: any) {
      console.error("Payment checkout error", err);
      setError(err?.message || "An unexpected error occurred.");
      setLoadingPlan(null);
    }
  };

  return ReactDOM.createPortal(
    <div className="su-modal-overlay" onClick={handleBackdropClick}>
      <div className="su-modal" style={{ maxWidth: "900px" }}>
        {/* Header */}
        <div className="su-modal-header">
          <div className="su-modal-title">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 2L2 7l10 5 10-5-10-5z" />
              <path d="M2 17l10 5 10-5" />
              <path d="M2 12l10 5 10-5" />
            </svg>
            <span>UPGRADE YOUR PLAN</span>
          </div>
          <button type="button" className="su-modal-close" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        {/* Body */}
        <div className="su-modal-body" style={{ padding: "24px" }}>
          <p style={{ color: "#9CA3AF", marginBottom: "24px", textAlign: "center" }}>
            Unlock more features and grow your investor relations.
          </p>

          {error && (
            <div
              style={{
                backgroundColor: "rgba(239, 68, 68, 0.1)",
                border: "1px solid #EF4444",
                color: "#EF4444",
                padding: "12px",
                marginBottom: "16px",
                fontSize: "14px",
              }}
            >
              {error}
            </div>
          )}

          {/* Plan Cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "12px" }}>
            {planDefinitions.map((plan) => {
              const isSelected = plan.id === selectedPlan;
              const isLoading = loadingPlan === plan.id;
              const isCurrent = isCurrentPlan(plan.id);
              const cannotSelect = isCurrent || isDowngrade(plan.id);

              return (
                <div
                  key={plan.id}
                  onClick={() => !cannotSelect && handleSelectPlan(plan.id)}
                  style={{
                    border: isCurrent
                      ? "2px solid #10B981"
                      : isSelected
                      ? "2px solid #A855F7"
                      : "1px solid #374151",
                    padding: "16px",
                    backgroundColor: isCurrent
                      ? "rgba(16, 185, 129, 0.1)"
                      : isSelected
                      ? "rgba(168, 85, 247, 0.1)"
                      : "#0a0a0a",
                    cursor: cannotSelect || loadingPlan ? "default" : "pointer",
                    transition: "all 0.2s ease",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                  }}
                >
                  {/* Current Plan Badge */}
                  {isCurrent && (
                    <span
                      style={{
                        position: "absolute",
                        top: "-10px",
                        right: "10px",
                        backgroundColor: "#10B981",
                        color: "#FFFFFF",
                        fontSize: "10px",
                        padding: "2px 8px",
                        fontWeight: "bold",
                      }}
                    >
                      CURRENT
                    </span>
                  )}

                  <h3
                    style={{
                      fontSize: "16px",
                      fontWeight: "bold",
                      marginBottom: "8px",
                      color: "#FFFFFF",
                    }}
                  >
                    {plan.title}
                  </h3>
                  <div style={{ marginBottom: "8px" }}>
                    <span
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#FFFFFF",
                      }}
                    >
                      {plan.price}
                    </span>
                    {plan.period && (
                      <span style={{ fontSize: "12px", color: "#9CA3AF" }}>
                        {plan.period}
                      </span>
                    )}
                  </div>
                  <p
                    style={{
                      fontSize: "12px",
                      color: "#9CA3AF",
                      marginBottom: "12px",
                    }}
                  >
                    {plan.description}
                  </p>

                  {/* Features */}
                  <ul style={{ listStyle: "none", padding: 0, margin: "0 0 16px 0", flexGrow: 1 }}>
                    {plan.features.map((feature, idx) => (
                      <li
                        key={idx}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          fontSize: "11px",
                          color: "#D1D5DB",
                          marginBottom: "4px",
                        }}
                      >
                        <CheckIcon style={{ color: "#10B981", width: "12px", height: "12px" }} />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCheckout(plan.id);
                    }}
                    disabled={isCurrent || isDowngrade(plan.id) || loadingPlan !== null}
                    style={{
                      width: "100%",
                      padding: "10px",
                      backgroundColor: isCurrent
                        ? "#1F2937"
                        : plan.isContactSales
                        ? "transparent"
                        : isSelected
                        ? "#A855F7"
                        : "#000000",
                      color: isCurrent || isDowngrade(plan.id) ? "#6B7280" : "#FFFFFF",
                      border: plan.isContactSales
                        ? "1px solid #6B7280"
                        : isCurrent || isDowngrade(plan.id)
                        ? "1px solid #374151"
                        : "1px solid #FFFFFF",
                      cursor: isCurrent || isDowngrade(plan.id) || loadingPlan ? "not-allowed" : "pointer",
                      fontWeight: "600",
                      fontSize: "12px",
                      transition: "all 0.2s ease",
                      marginTop: "auto",
                    }}
                  >
                    {isLoading
                      ? "Processing..."
                      : isCurrent
                      ? "Current Plan"
                      : isDowngrade(plan.id)
                      ? "Downgrade"
                      : plan.buttonText}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="su-modal-footer">
          <button
            type="button"
            className="su-btn-secondary"
            onClick={onClose}
            style={{ width: "100%" }}
          >
            CANCEL
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UpgradeModal;
